import { PaginatedList } from "../model/common/paginated-list";
import {
	CreateUtilityConfigurationPayload,
	EditUtilityConfigurationPayload,
	GetUtilityConfigurationsParams,
	UtilityConfiguration,
	UtilityConfigurationDetails,
	UtilityConfigurationLookup,
	UtilityFeatureGroup,
} from "../model/bps-admin/utility-configuration";
import { apiSlice } from "../stores/base/api-slice";

// BPS admin only — every route here sits behind the gateway's BpsAdminPolicy.
const serviceBaseUrl = "/utilityconfigurations/utility-configurations";
const lookupsBaseUrl = "/utilityconfigurations";

// Create and edit take the same multipart body, except that edit may omit the logo.
const toUtilityFormData = (payload: CreateUtilityConfigurationPayload | EditUtilityConfigurationPayload) => {
	const body = new FormData();
	body.append("Name", payload.name);
	body.append("Acronym", payload.acronym);
	body.append("SubDomain", payload.subDomain);
	body.append("CurrencyId", payload.currencyId);
	body.append("CountryId", payload.countryId);
	body.append("Language", payload.language);
	body.append("PrimaryColor", payload.primaryColor);
	body.append("SecondaryColor", payload.secondaryColor);
	body.append("AccentColor", payload.accentColor);
	if (payload.logo) body.append("Logo", payload.logo);
	// Repeated keys, which is how ASP.NET binds a List<Guid> from a form. An empty
	// list means the key is absent — there is no way to spell "[]" in multipart.
	payload.customerFeatureFlags.forEach(id => body.append("CustomerFeatureFlags", id));
	payload.adminFeatureFlags.forEach(id => body.append("AdminFeatureFlags", id));

	return body;
};

const utilityConfigurationEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getUtilityConfigurations: builder.query<
			PaginatedList<UtilityConfiguration>,
			GetUtilityConfigurationsParams
		>({
			query: ({ page, pageSize, search, isActive }) => ({
				url: serviceBaseUrl,
				method: "GET",
				params: {
					Page: page,
					PageSize: pageSize,
					...(search ? { Search: search } : {}),
					...(isActive === undefined ? {} : { IsActive: isActive }),
				},
			}),
			providesTags: ["UtilityConfigurations"],
		}),
		getUtilityConfiguration: builder.query<UtilityConfigurationDetails, string>({
			query: id => `${serviceBaseUrl}/${id}`,
			providesTags: ["UtilityConfigurations"],
		}),
		getUtilityCountries: builder.query<UtilityConfigurationLookup[], void>({
			query: () => `${lookupsBaseUrl}/countries`,
		}),
		getUtilityCurrencies: builder.query<UtilityConfigurationLookup[], void>({
			query: () => `${lookupsBaseUrl}/currencies`,
		}),
		// The catalogue of features that exist, not the ones a utility has switched on —
		// those values live in Flagsmith and come from the feature-flags endpoint.
		getUtilityFeatureGroups: builder.query<UtilityFeatureGroup[], void>({
			query: () => `${serviceBaseUrl}/feature-groups`,
		}),
		// Multipart, because the endpoint takes the logo as an IFormFile.
		createUtilityConfiguration: builder.mutation<void, CreateUtilityConfigurationPayload>({
			query: payload => ({ url: serviceBaseUrl, method: "POST", body: toUtilityFormData(payload) }),
			invalidatesTags: ["UtilityConfigurations"],
		}),
		editUtilityConfiguration: builder.mutation<void, EditUtilityConfigurationPayload>({
			query: payload => ({
				url: `${serviceBaseUrl}/${payload.id}`,
				method: "PUT",
				body: toUtilityFormData(payload),
			}),
			invalidatesTags: ["UtilityConfigurations"],
		}),
		updateUtilityConfigurationStatus: builder.mutation<void, { id: string; isActive: boolean }>({
			query: ({ id, isActive }) => ({
				url: `${serviceBaseUrl}/${id}/status`,
				method: "PUT",
				body: { isActive },
			}),
			invalidatesTags: ["UtilityConfigurations"],
		}),
	}),
});

export const {
	useGetUtilityConfigurationsQuery,
	useGetUtilityConfigurationQuery,
	useGetUtilityCountriesQuery,
	useGetUtilityCurrenciesQuery,
	useGetUtilityFeatureGroupsQuery,
	useCreateUtilityConfigurationMutation,
	useEditUtilityConfigurationMutation,
	useUpdateUtilityConfigurationStatusMutation,
} = utilityConfigurationEndpoints;
